import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import mockData from '../../mockData/incidents.json';
import {
  HiOutlineExclamationTriangle,
  HiOutlineShieldCheck,
  HiOutlineCurrencyDollar,
  HiOutlineClipboardDocumentCheck
} from 'react-icons/hi2';

import { SEMANTIC_SUCCESS_MID } from '../../theme/chartColors';
const COLORS = ['#3b82f6', SEMANTIC_SUCCESS_MID, '#f59e0b', '#ef4444', '#8b5cf6'];

const IncidentAnalyticsDashboard = () => {
  const [allIncidents, setAllIncidents] = useState([]);
  const [filterPlant, setFilterPlant] = useState("");
  const [filterDateStr, setFilterDateStr] = useState("");

  useEffect(() => {
    // In actual implementation, this would fetch from an API
    setAllIncidents(mockData);
  }, []);

  // Apply filters
  const incidents = allIncidents.filter(inc => {
    if (filterPlant && inc.plantName !== filterPlant) return false;
    if (filterDateStr && !inc.incidentDate.startsWith(filterDateStr)) return false;
    return true;
  });

  // Compute metrics
  const totalIncidents = incidents.length;
  const closedIncidents = incidents.filter(i => i.currentStep === 7).length;
  const openInvestigations = incidents.filter(i => i.currentStep === 4).length;

  // Total Cost computation
  const totalCost = incidents.reduce((sum, inc) => {
    if (inc.costs) {
      return sum + (inc.costs.totalCost || 0);
    }
    return sum;
  }, 0);

  // Group by Type (Pie Chart)
  const typeCounts = incidents.reduce((acc, inc) => {
    acc[inc.type] = (acc[inc.type] || 0) + 1;
    return acc;
  }, {});
  const typeChartData = Object.keys(typeCounts).map(key => ({
    name: key,
    value: typeCounts[key]
  }));

  // Group by Plant (Bar Chart)
  const plantCounts = incidents.reduce((acc, inc) => {
    acc[inc.plantName] = (acc[inc.plantName] || 0) + 1;
    return acc;
  }, {});
  const plantChartData = Object.keys(plantCounts).map(key => ({
    name: key,
    Incidents: plantCounts[key]
  }));

  // Group by Month (Line Chart)
  // Mock data has dates like "2023-10-15..." Let's format to YYYY-MM
  const monthCounts = incidents.reduce((acc, inc) => {
    const d = new Date(inc.incidentDate);
    if (!isNaN(d)) {
      const month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      acc[month] = (acc[month] || 0) + 1;
    }
    return acc;
  }, {});
  const monthChartData = Object.keys(monthCounts).sort().map(key => ({
    month: key,
    count: monthCounts[key]
  }));

  // Injury Types Chart Data
  const injuryCounts = incidents.reduce((acc, inc) => {
    if (inc.type === 'Injury' && inc.injuryDetails && inc.injuryDetails.injuryType) {
      const type = inc.injuryDetails.injuryType;
      acc[type] = (acc[type] || 0) + 1;
    }
    return acc;
  }, { 'FAC': 0, 'MTC': 0, 'LTC': 0, 'FATAL': 0 });
  const injuryTypeData = Object.keys(injuryCounts).map(key => ({
    name: key,
    count: injuryCounts[key]
  }));

  // Body Parts Injured Chart Data
  const bodyPartCounts = incidents.reduce((acc, inc) => {
    if (inc.type === 'Injury' && inc.injuryDetails && inc.injuryDetails.bodyPart) {
      const part = inc.injuryDetails.bodyPart;
      acc[part] = (acc[part] || 0) + 1;
    }
    return acc;
  }, {});
  const bodyPartData = Object.keys(bodyPartCounts).map(key => ({
    name: key,
    Incidents: bodyPartCounts[key]
  })).sort((a, b) => b.Incidents - a.Incidents);

  // Heinrich Triangle Computations
  const heinrichData = {
    fatal: injuryCounts['FATAL'] || 0,
    major: (injuryCounts['LTC'] || 0),
    minor: (injuryCounts['MTC'] || 0) + (injuryCounts['FAC'] || 0),
    nearmiss: incidents.filter(i => i.type === 'Nearmiss').length
  };

  return (
    <div className="pagecontainer">
      <div className='page-header'>
        <div className='header-contain'>
          <h1>Incident Analytics Dashboard</h1>
          <p>High-level visibility and trends of the safety process</p>
        </div>
        <div className="header-contain gap-10">
          <select
            className="rounded-lg mr-2 border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-base py-2 px-3 bg-card"
            value={filterPlant}
            onChange={(e) => setFilterPlant(e.target.value)}
          >
            <option value="">All Plants</option>
            <option value="Plant A">Plant A</option>
            <option value="Plant B">Plant B</option>
            <option value="Plant 1">Plant 1</option>
            <option value="Plant 2">Plant 2</option>
          </select>

          <input
            type="date"
            className="rounded-lg border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 text-base py-2 px-3 bg-card"
            value={filterDateStr}
            onChange={(e) => setFilterDateStr(e.target.value)}
            title="Filter by incident date"
          />
        </div>
      </div>



      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <div className="bg-card p-4 rounded-xl border-l-[3px] border-blue-500 shadow-sm flex items-center">
          <div className="p-3 bg-blue-50 text-blue-600 rounded-lg mr-4">
            <HiOutlineExclamationTriangle className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm text-tertiary font-medium">Total Incidents</p>
            <p className="text-2xl font-bold text-primary">{totalIncidents}</p>
          </div>
        </div>
        <div className="bg-card p-4 rounded-xl border-l-[3px] border-green-500 shadow-sm flex items-center">
          <div className="p-3 bg-green-50 text-green-600 rounded-lg mr-4">
            <HiOutlineShieldCheck className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm text-tertiary font-medium">Closed Incidents</p>
            <p className="text-2xl font-bold text-primary">{closedIncidents}</p>
          </div>
        </div>
        <div className="bg-card p-4 rounded-xl border-l-[3px] border-yellow-500 shadow-sm flex items-center">
          <div className="p-3 bg-yellow-50 text-yellow-600 rounded-lg mr-4">
            <HiOutlineClipboardDocumentCheck className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm text-tertiary font-medium">Open Investigations</p>
            <p className="text-2xl font-bold text-primary">{openInvestigations}</p>
          </div>
        </div>
        <div className="bg-card p-4 rounded-xl border-l-[3px] border-purple-500 shadow-sm flex items-center">
          <div className="p-3 bg-purple-50 text-purple-600 rounded-lg mr-4">
            <HiOutlineCurrencyDollar className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm text-tertiary font-medium">Total Cost Impact</p>
            <p className="text-2xl font-bold text-primary">${totalCost.toLocaleString()}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Incident Types Chart */}
        <div className="bg-card p-5 rounded-xl border border-gray-200 shadow-sm">
          <h3 className="font-semibold text-primary mb-4">Incidents by Type</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={typeChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  fill="#8884d8"
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {typeChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Incidents by Month Trend */}
        <div className="bg-card p-5 rounded-xl border border-gray-200 shadow-sm">
          <h3 className="font-semibold text-primary mb-4">Incident Trends (Monthly)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthChartData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis dataKey="month" axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} />
                <Tooltip cursor={{ stroke: '#9CA3AF', strokeWidth: 1 }} />
                <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={3} dot={{ r: 4 }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        {/* Incidents by Plant */}
        <div className="bg-card p-5 rounded-xl border border-gray-200 shadow-sm">
          <h3 className="font-semibold text-primary mb-4">Incidents by Plant</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={plantChartData} layout="vertical" margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#E5E7EB" />
                <XAxis type="number" axisLine={false} tickLine={false} />
                <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} width={100} />
                <Tooltip cursor={{ fill: '#F3F4F6' }} />
                <Bar dataKey="Incidents" fill={SEMANTIC_SUCCESS_MID} radius={[0, 4, 4, 0]} barSize={24} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Heinrich Triangle */}
        <div className="bg-card p-5 rounded-xl border border-gray-200 shadow-sm">
          <h3 className="font-semibold text-primary mb-4">Heinrich Accident Triangle</h3>
          <div className="h-72 flex justify-center items-center">
            <div className="w-64 h-64 flex flex-col" style={{ clipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)' }}>
              <div className="h-1/4 bg-red-500 flex flex-col items-center justify-end pb-1 text-white font-bold leading-tight relative border-b border-red-600">
                <span className="text-base">{heinrichData.fatal}</span>
                <span className="text-[10px] uppercase opacity-90 tracking-wider">Fatal</span>
              </div>
              <div className="h-1/4 bg-orange-400 flex flex-col items-center justify-end pb-1 text-white font-bold leading-tight border-b border-orange-500">
                <span className="text-base">{heinrichData.major}</span>
                <span className="text-[10px] uppercase opacity-90 tracking-wider">Major (LTC)</span>
              </div>
              <div className="h-1/4 bg-yellow-400 flex flex-col items-center justify-end pb-1 text-white font-bold leading-tight border-b border-yellow-500">
                <span className="text-base">{heinrichData.minor}</span>
                <span className="text-[10px] uppercase opacity-90 tracking-wider">Minor (MTC/FAC)</span>
              </div>
              <div className="h-1/4 bg-green-400 flex flex-col items-center justify-start pt-2 text-white font-bold leading-tight">
                <span className="text-base">{heinrichData.nearmiss}</span>
                <span className="text-[10px] uppercase opacity-90 tracking-wider">Near Miss</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Injury Severity (FAC/MTC/LTC/FATAL) */}
        <div className="bg-card p-5 rounded-xl border border-gray-200 shadow-sm">
          <h3 className="font-semibold text-primary mb-4">Injury Severity Breakdown</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={injuryTypeData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: '#F3F4F6' }} />
                <Bar dataKey="count" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={40}>
                  {injuryTypeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={
                      entry.name === 'FATAL' ? '#ef4444' :
                        entry.name === 'LTC' ? '#f59e0b' :
                          entry.name === 'MTC' ? '#eab308' : '#3b82f6'
                    }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Body Parts Injured */}
        <div className="bg-card p-5 rounded-xl border border-gray-200 shadow-sm">
          <h3 className="font-semibold text-primary mb-4">Body Parts Injured</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bodyPartData} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} stroke="#E5E7EB" />
                <XAxis type="number" axisLine={false} tickLine={false} allowDecimals={false} />
                <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} width={100} />
                <Tooltip cursor={{ fill: '#F3F4F6' }} />
                <Bar dataKey="Incidents" fill="#8b5cf6" radius={[0, 4, 4, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncidentAnalyticsDashboard;
